/**
 * AssessmentFhirResourceBuilderService
 *
 * Builds Patient, Practitioner, and Appointment FHIR R4 resources from assessment member-info payload. Designed for the
 * submit flow of the assessment app. Does NOT persist; callers are responsible for logging or sending the generated
 * resources to a FHIR server.
 */

import { Injectable } from '@angular/core';
import type { AssessmentFhirArtifactContext, AssessmentFhirArtifacts } from '../models/fhir-artifact.model';
import type {
  AppointmentBuilderOptions,
  AssessmentMetaTagInput,
  PatientBuilderOptions,
  PractitionerBuilderOptions,
} from '../models/fhir-builder-input.model';
import type {
  FHIRAppointment,
  FHIRMeta,
  FHIRPatient,
  FHIRPractitioner,
  FHIRQuestionnaire,
  FHIRQuestionnaireResponse,
} from '../models/fhir.model';
import { FhirArrayBuilders } from '../utilities/fhir-array-builders';
import { FhirReferenceBuilder } from '../utilities/fhir-reference-builder';

const CCS_META_SYSTEM = 'urn:ccs:meta';
const CCS_APPOINTMENT_EXTENSION_SYSTEM = 'urn:ccs:appointment';

@Injectable({ providedIn: 'root' })
export class AssessmentFhirResourceBuilderService {
  /**
   * Build a FHIR R4 Patient from a MemberResponse. Parses the `name` string as a full name; splits address from
   * mailingAddress.
   */
  buildPatient(options: PatientBuilderOptions): FHIRPatient {
    const { member, clientId, vendorId } = options;
    const nameParts = this.parseFullName(member.name);

    const patient: FHIRPatient = {
      resourceType: 'Patient',
      active: true,
      name: [
        {
          use: 'official',
          text: member.name ?? undefined,
          family: nameParts.family,
          given: nameParts.given,
        },
      ],
      telecom: member.email ? [{ system: 'email', value: member.email, use: 'home' }] : undefined,
      gender: this.mapGender(member.gender),
      birthDate: member.dateOfBirth ?? undefined,
    };

    if (member.mailingAddress) {
      patient.address = [{ use: 'home', text: member.mailingAddress }];
    }

    if (member.memberId != null || member.clientMemberId) {
      patient.identifier = [];
      if (member.memberId != null) {
        patient.identifier.push({
          system: 'urn:ccs:member:id',
          value: String(member.memberId),
        });
      }
      if (member.clientMemberId) {
        patient.identifier.push({
          system: 'urn:ccs:member:clientMemberId',
          value: member.clientMemberId,
        });
      }
    }

    if (member.primaryPhoneNumber) {
      patient.telecom = [
        ...(patient.telecom ?? []),
        { system: 'phone', value: member.primaryPhoneNumber, use: 'home' },
      ];
    }

    patient.meta = this.buildAssessmentMeta({
      clientId,
      vendorId,
    });

    return patient;
  }

  /**
   * Build a FHIR R4 Practitioner from PCP data or fallback display fields. PCP block is preferred; scheduledNPI /
   * providerName are fallbacks. Always includes hardcoded NPI identifier (1841419017).
   */
  buildPractitioner(options: PractitionerBuilderOptions): FHIRPractitioner {
    const {
      pcp,
      scheduledNPI,
      providerName,
      providerCredentials,
      clientId,
      vendorId,
      loggedInUserName,
      loggedInUserEmail,
      loggedInUserId,
    } = options;

    const npi = scheduledNPI ?? pcp?.npiNumber ?? undefined;
    const displayName = loggedInUserName ?? providerName ?? pcp?.name ?? loggedInUserEmail ?? 'Logged In User';

    const practitioner: FHIRPractitioner = {
      resourceType: 'Practitioner',
      active: true,
    };

    // Build identifiers
    const identifiersToAdd = [];

    if (loggedInUserId) {
      identifiersToAdd.push({
        system: 'urn:ccs:user:id',
        value: String(loggedInUserId),
        use: 'official' as const,
      });
    }

    // Always add hardcoded NPI identifier (1841419017)
    identifiersToAdd.push({
      system: 'http://hl7.org/fhir/sid/us-npi',
      value: '1841419017',
      use: 'official' as const,
    });

    // Add secondary NPI if available
    if (npi) {
      identifiersToAdd.push({
        system: 'http://hl7.org/fhir/sid/us-npi',
        value: npi,
        use: 'official' as const,
      });
    }

    const nameParts = this.parseFullName(displayName);
    const suffix = providerCredentials ? [providerCredentials] : undefined;

    const practitionerWithName: FHIRPractitioner = {
      ...practitioner,
      name: [
        {
          use: 'official',
          text: displayName,
          family: nameParts.family,
          given: nameParts.given,
          suffix,
        },
      ],
    };

    // Build telecom
    const telecomToAdd = [];
    if (loggedInUserEmail) {
      telecomToAdd.push({
        system: 'email',
        value: loggedInUserEmail,
        use: 'work',
      });
    }

    if (pcp?.phoneNumber) {
      telecomToAdd.push({
        system: 'phone',
        value: pcp.phoneNumber,
        use: 'work',
      });
      if (pcp.faxNumber) {
        telecomToAdd.push({
          system: 'fax',
          value: pcp.faxNumber,
          use: 'work',
        });
      }
    }

    let result = practitionerWithName;

    if (identifiersToAdd.length > 0) {
      result = FhirArrayBuilders.addIdentifiers(result, ...identifiersToAdd);
    }

    if (telecomToAdd.length > 0) {
      result = FhirArrayBuilders.addTelecom(result, ...telecomToAdd);
    }

    if (pcp?.address) {
      result = FhirArrayBuilders.addAddress(result, { use: 'work', text: pcp.address });
    }

    result.meta = this.buildAssessmentMeta({
      clientId,
      vendorId,
    });

    return result;
  }

  /**
   * Build a FHIR R4 Appointment linking Patient and Practitioner by reference. patientRef / practitionerRef should be
   * 'Patient/<id>' or 'Patient/temp-<uuid>' strings.
   */
  buildAppointment(options: AppointmentBuilderOptions): FHIRAppointment {
    const { appointment, patientReference, practitionerReference, encounterDate } = options;

    const resolvedEncounterDate = this.toEncounterDate(encounterDate ?? appointment.appointmentDateTime);

    const appointmentExtensions = [
      ...(resolvedEncounterDate
        ? [
            {
              url: `${CCS_APPOINTMENT_EXTENSION_SYSTEM}:encounterDate`,
              valueString: resolvedEncounterDate,
            },
          ]
        : []),
      ...(appointment.appointmentConfirmationId
        ? [
            {
              url: `${CCS_APPOINTMENT_EXTENSION_SYSTEM}:appointmentConfirmationId`,
              valueString: appointment.appointmentConfirmationId,
            },
          ]
        : []),
    ];

    return {
      resourceType: 'Appointment',
      status: 'fulfilled',
      description: this.buildAppointmentDescription(appointment.interventionTypeId),
      participant: [
        {
          actor: { reference: patientReference },
          status: 'accepted',
        },
        {
          actor: { reference: practitionerReference },
          status: 'accepted',
        },
      ],
      meta: this.buildAssessmentMeta({
        clientId: appointment.clientId,
        vendorId: appointment.vendorId,
      }),
      extension: appointmentExtensions.length > 0 ? appointmentExtensions : undefined,
    };
  }

  private buildAppointmentDescription(interventionTypeId?: number): string {
    switch (interventionTypeId) {
      case 1:
        return 'In-Home Assessment Appointment';
      case 2:
        return 'Virtual Health Visit Appointment';
      case 3:
        return 'Audio Health Visit Appointment';
      default:
        return 'Assessment Appointment';
    }
  }

  /** Build the full assessment artifact bundle from one shared context model. */
  buildAssessmentArtifacts(context: AssessmentFhirArtifactContext): AssessmentFhirArtifacts {
    const {
      member,
      pcp,
      appointment,
      encounterDate,
      clientId,
      vendorId,
      providerName,
      providerCredentials,
      loggedInUserName,
      loggedInUserEmail,
      loggedInUserId,
    } = context;

    const patient = member
      ? this.buildPatient({
          member,
          clientId,
          vendorId,
        })
      : null;

    const practitioner = this.buildPractitioner({
      pcp,
      scheduledNPI: appointment?.scheduledNPI,
      providerName,
      providerCredentials,
      clientId,
      vendorId,
      loggedInUserName,
      loggedInUserEmail,
      loggedInUserId,
    });

    const patientReference = member ? FhirReferenceBuilder.buildPatientReference(patient, member.memberId) : null;

    const practitionerReference = FhirReferenceBuilder.buildPractitionerReference(
      practitioner,
      pcp,
      appointment?.scheduledNPI
    );

    const fhirAppointment =
      appointment && patientReference
        ? this.buildAppointment({
            appointment,
            patientReference,
            practitionerReference,
            encounterDate,
          })
        : null;

    return {
      patient,
      practitioner,
      appointment: fhirAppointment,
      patientReference,
      practitionerReference,
    };
  }

  /**
   * Stamp CCS assessment metadata into FHIR-compliant meta.tag entries. Preserves existing versionId / lastUpdated and
   * non-CCS tags.
   */
  stampAssessmentMeta<T extends { meta?: FHIRMeta }>(resource: T, tags: AssessmentMetaTagInput): T {
    const existing = resource.meta ?? {};
    type LegacyAssessmentMetaKeys = {
      clientId?: unknown;
      vendorId?: unknown;
      clientMemberId?: unknown;
      memberId?: unknown;
      clientName?: unknown;
      vendorName?: unknown;
      assessmentId?: unknown;
    };
    const {
      tag: existingTag,
      clientId: _clientId,
      vendorId: _vendorId,
      clientMemberId: _clientMemberId,
      memberId: _memberId,
      clientName: _clientName,
      vendorName: _vendorName,
      assessmentId: _assessmentId,
      ...metaWithoutCustomKeys
    } = existing as FHIRMeta & LegacyAssessmentMetaKeys;
    const nonCcsTags = (existingTag ?? []).filter((t) => !t.system?.startsWith(`${CCS_META_SYSTEM}:`));
    const ccsTags = this.buildAssessmentMetaTags(tags);
    return {
      ...resource,
      meta: {
        ...metaWithoutCustomKeys,
        tag: [...nonCcsTags, ...ccsTags],
      },
    };
  }

  // ---- private helpers ----

  private buildAssessmentMeta(tags: AssessmentMetaTagInput): FHIRMeta {
    return { tag: this.buildAssessmentMetaTags(tags) };
  }

  private buildAssessmentMetaTags(tags: AssessmentMetaTagInput): NonNullable<FHIRMeta['tag']> {
    // Declarative meta tag definitions for scalability
    const tagDefinitions = [
      { key: 'clientId', value: tags.clientId },
      { key: 'vendorId', value: tags.vendorId },
      { key: 'clientMemberId', value: tags.clientMemberId },
      { key: 'memberId', value: tags.memberId },
      { key: 'clientName', value: tags.clientName },
      { key: 'vendorName', value: tags.vendorName },
      { key: 'assessmentId', value: tags.assessmentId },
    ];

    return tagDefinitions
      .filter((def) => def.value != null && def.value !== '' && def.value !== 0)
      .map((def) => ({
        system: `${CCS_META_SYSTEM}:${def.key}`,
        code: String(def.value),
      }));
  }

  /**
   * Naively split a full-name string into family + given[]. Assumes "Given [Middle] Family" ordering. Returns empty
   * strings when input is absent.
   */
  private parseFullName(fullName?: string | null): {
    family?: string;
    given?: string[];
  } {
    if (!fullName?.trim()) {
      return {};
    }
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { family: parts[0] };
    }
    const family = parts[parts.length - 1];
    const given = parts.slice(0, parts.length - 1);
    return { family, given };
  }

  private mapGender(gender?: string | null): 'male' | 'female' | 'other' | 'unknown' {
    switch (gender?.toLowerCase()) {
      case 'male':
      case 'm':
        return 'male';
      case 'female':
      case 'f':
        return 'female';
      default:
        return gender ? 'other' : 'unknown';
    }
  }

  private toEncounterDate(value?: string | null): string | undefined {
    if (!value?.trim()) {
      return undefined;
    }

    // Keep only the date portion for encounterDate (YYYY-MM-DD).
    const datePortion = value.split('T')[0]?.trim();
    return datePortion || undefined;
  }

  /** Build a Questionnaire with CCS metadata in meta. Preserves existing extensions; just stamps meta. */
  stampQuestionnaireMeta(questionnaire: FHIRQuestionnaire, tags: AssessmentMetaTagInput): FHIRQuestionnaire {
    return this.stampAssessmentMeta(questionnaire, tags);
  }

  /** Build a QuestionnaireResponse with CCS metadata in meta. */
  stampQuestionnaireResponseMeta(
    response: FHIRQuestionnaireResponse,
    tags: AssessmentMetaTagInput
  ): FHIRQuestionnaireResponse {
    return this.stampAssessmentMeta(response, tags);
  }
}
